import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl } from '@material-ui/core';
import { ModalActionsComponent, PasslySelect, PasslyCheckbox, PasslyTextField } from '@app/components';
import { getLoaderBtn, getModalSwitch, handleGetProvisioningPoliciesDTOData } from '@app/store/actions';
import { IProvisioningPolicyDTO } from '@app/models';
import { syncService } from '@app/services';
import { useTypedTranslation } from '@app/hooks';
import { onPressEnterModal } from '@app/helpers';
import { DirSyncStatuses } from '@app/enums/dirSync.enums';

export interface IEditAgentData {
    onCompleteCallback: Function;
}

export default function AddAUSIAgentDialog(props: IEditAgentData) {
    const { onCompleteCallback } = props;
    const provisioningPolicies: IProvisioningPolicyDTO[] = useSelector((state: any) => state.provisioningPolicies.provisioningPoliciesDTO);
    const [provisioningPolicySelected, setProvisioningPolicySelected] = useState('');
    const [agentName, setAgentName] = useState('');
    const [agentNameInvalid, setAgentNameInvalid] = useState(false);
    const [changePasswordEnabled, setChangePasswordEnabled] = useState(false);
    const [error, setError] = useState('');

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    useEffect(() => {
        dispatch(handleGetProvisioningPoliciesDTOData());
    }, []);

    const setInitialPolicy = () => {
        if (provisioningPolicies?.length > 0) {
            setProvisioningPolicySelected(provisioningPolicies[0].Id);
        }
    };

    useEffect(() => {
        setInitialPolicy();
    }, [provisioningPolicies]);

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'agentName':
                setAgentName(value);
                setAgentNameInvalid(!value);
                break;
            case 'provisioningPolicySelected':
                setProvisioningPolicySelected(value);
                break;
            default:
                break;
        }
    };

    const onCheckboxChange = (checked: boolean) => {
        setChangePasswordEnabled(checked);
    };

    const handlePatchSuccess = () => {
        onCompleteCallback();
    };

    const handlePatchError = (error: any) => {
        dispatch(getLoaderBtn(false));

        const err = translation(
            error?.response?.data[0]?.errorCode === 'already_exist'
            ? 'INVALID_TAKEN_AGENT_NAME'
            : 'EDIT_AGENT_ERROR'
        );

        setError(err);
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));
        const agent = {
            agentName: agentName,
            provisioningPolicyId: provisioningPolicySelected,
            passwordPolicy: changePasswordEnabled,
            provisioningState: DirSyncStatuses.Created
        };
        syncService.addAUSISyncAgent(agent, handlePatchSuccess, handlePatchError);
    };

    const handleClose = () => {
        dispatch(getLoaderBtn(false));
        setAgentName('');
        setChangePasswordEnabled(false);
        setInitialPolicy();
        dispatch(getModalSwitch(false));
    };

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, agentNameInvalid)}>
                <FormControl>
                    <PasslyTextField
                        fullWidth
                        name='agentName'
                        label={'AGENT_NAME'}
                        value={agentName}
                        onChange={onFormChange}
                        id='agentName-input'
                        inputProps={{ maxLength: 250 }}
                        error={agentNameInvalid}
                        helperText={agentNameInvalid ? translation('REQUIRED_FIELD') : ''}
                        autoFocus />
                </FormControl>

                <FormControl>
                    <PasslySelect
                        name='provisioningPolicySelected'
                        value={provisioningPolicySelected}
                        onChange={onFormChange}
                        id='selectProvisioning-input'
                        label={'PROVISIONING_POLICY'}
                        optionLabel='DisplayName'
                        optionValue='Id'
                        options={provisioningPolicies}
                        appendTo={document.body}
                    />
                </FormControl>

                <FormControl>
                    <h4>{translation('SYNC_PASSWORD_POLICY')}</h4>
                    <PasslyCheckbox
                        name='changePasswordEnabled'
                        checked={changePasswordEnabled}
                        onChange={onCheckboxChange}
                        label={translation('ENABLE_PASSWORD_WRITEBACK_CHECK')}
                    />
                </FormControl>

                {error && (
                    <p>
                        <div className='modal__error'>{error}</div>
                    </p>
                )}
            </form>
            <ModalActionsComponent
                primaryBtn={translation('ADD_AGENT')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleClose}
                disabled={!agentName || agentNameInvalid || !provisioningPolicySelected}
            />
        </>
    );
}
